/**
 * Hook pour gérer les permissions de l'utilisateur avec la nouvelle architecture multi-tenant
 */
import { useState, useEffect, useCallback } from 'react';
import multiTenantPermissionService from '../services/multiTenantPermissionService';
import { Permission, PermissionCode, PlatformRoleName, OperationalRoleName } from '@/types/permissions';

export const usePermissions = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [permissionCodes, setPermissionCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const initializePermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Vérifier si les permissions sont déjà initialisées
      if (multiTenantPermissionService.isUserInitialized()) {
        const userPermissions = multiTenantPermissionService.getUserPermissions();
        const userRoles = multiTenantPermissionService.getCurrentUserRoles();
        const userPermissionCodes = multiTenantPermissionService.getUserPermissionCodes();
        
        setPermissions(userPermissions);
        setRoles(userRoles);
        setPermissionCodes(userPermissionCodes);
        setLoading(false);
        return;
      }
      
      await multiTenantPermissionService.initializeUserPermissions();
      
      // Vérifier si l'initialisation a réussi
      if (!multiTenantPermissionService.isUserInitialized()) {
        setError('Permissions non initialisées');
        return;
      }
      
      const userPermissions = multiTenantPermissionService.getUserPermissions();
      const userRoles = multiTenantPermissionService.getCurrentUserRoles();
      const userPermissionCodes = multiTenantPermissionService.getUserPermissionCodes();
      
      setPermissions(userPermissions);
      setRoles(userRoles);
      setPermissionCodes(userPermissionCodes);
      
    } catch (err: any) {
      
      // Si c'est une erreur d'authentification, ne pas définir d'erreur
      if (err?.response?.status === 401) {
        setError(null);
      } else {
        setError('Erreur lors du chargement des permissions');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshPermissions = useCallback(async () => {
    await multiTenantPermissionService.refreshUserPermissions();
    await initializePermissions();
  }, [initializePermissions]);

  const hasPermission = useCallback((permissionCode: PermissionCode): boolean => {
    return multiTenantPermissionService.hasPermission(permissionCode);
  }, []);

  const hasAnyPermission = useCallback((permissionCodes: PermissionCode[]): boolean => {
    return multiTenantPermissionService.hasAnyPermission(permissionCodes);
  }, []);

  const hasAllPermissions = useCallback((permissionCodes: PermissionCode[]): boolean => {
    return multiTenantPermissionService.hasAllPermissions(permissionCodes);
  }, []);

  const hasRole = useCallback((roleName: PlatformRoleName | OperationalRoleName): boolean => {
    return multiTenantPermissionService.hasRole(roleName);
  }, []);

  const hasAnyRole = useCallback((roleNames: (PlatformRoleName | OperationalRoleName)[]): boolean => {
    return multiTenantPermissionService.hasAnyRole(roleNames);
  }, []);

  const hasAllRoles = useCallback((roleNames: (PlatformRoleName | OperationalRoleName)[]): boolean => {
    return multiTenantPermissionService.hasAllRoles(roleNames);
  }, []);

  // Méthodes spécifiques aux rôles de plateforme
  const isPlatformSuperAdmin = useCallback((): boolean => {
    return multiTenantPermissionService.isPlatformSuperAdmin();
  }, []);

  const isPlatformAdmin = useCallback((): boolean => {
    return multiTenantPermissionService.isPlatformAdmin();
  }, []);

  const isPlatformSupport = useCallback((): boolean => {
    return multiTenantPermissionService.isPlatformSupport();
  }, []);

  const hasPlatformRights = useCallback((): boolean => {
    return multiTenantPermissionService.hasPlatformRights();
  }, []);

  // Méthodes spécifiques aux rôles opérationnels
  const isTenantOwner = useCallback((): boolean => {
    return multiTenantPermissionService.isTenantOwner();
  }, []);

  const isTenantAdmin = useCallback((): boolean => {
    return multiTenantPermissionService.isTenantAdmin();
  }, []);

  const isTenantManager = useCallback((): boolean => {
    return multiTenantPermissionService.isTenantManager();
  }, []);

  const isSiteAdmin = useCallback((): boolean => {
    return multiTenantPermissionService.isSiteAdmin();
  }, []);

  const isSiteManager = useCallback((): boolean => {
    return multiTenantPermissionService.isSiteManager();
  }, []);

  const isSiteSupervisor = useCallback((): boolean => {
    return multiTenantPermissionService.isSiteSupervisor();
  }, []);

  const isQualityAgent = useCallback((): boolean => {
    return multiTenantPermissionService.isQualityAgent();
  }, []);

  const isOperator = useCallback((): boolean => {
    return multiTenantPermissionService.isOperator();
  }, []);

  const isStoreManager = useCallback((): boolean => {
    return multiTenantPermissionService.isStoreManager();
  }, []);

  const isViewer = useCallback((): boolean => {
    return multiTenantPermissionService.isViewer();
  }, []);

  // Méthodes de vérification de contexte multi-tenant
  const canManageTenants = useCallback((): boolean => {
    return multiTenantPermissionService.canManageTenants();
  }, []);

  const canManageSites = useCallback((): boolean => {
    return multiTenantPermissionService.canManageSites();
  }, []);

  const canManageTenantUsers = useCallback((): boolean => {
    return multiTenantPermissionService.canManageTenantUsers();
  }, []);

  const canPerformWeighing = useCallback((): boolean => {
    return multiTenantPermissionService.canPerformWeighing();
  }, []);

  const canManageStock = useCallback((): boolean => {
    return multiTenantPermissionService.canManageStock();
  }, []);

  const canPerformQualityAnalysis = useCallback((): boolean => {
    return multiTenantPermissionService.canPerformQualityAnalysis();
  }, []);

  const getPermissionsByCategory = useCallback(() => {
    return multiTenantPermissionService.getPermissionsByCategory();
  }, []);

  const getCurrentTenant = useCallback(() => {
    return multiTenantPermissionService.getCurrentTenant();
  }, []);

  const getCurrentSite = useCallback(() => {
    return multiTenantPermissionService.getCurrentSite();
  }, []);

  const getCurrentSiteRole = useCallback(() => {
    return multiTenantPermissionService.getCurrentSiteRole();
  }, []);

  useEffect(() => {
    // Vérifier si l'utilisateur est authentifié avant d'initialiser les permissions
    const token = localStorage.getItem('access_token');
    if (token) {
      initializePermissions();
    } else {
      setLoading(false);
    }
  }, [initializePermissions]);

  return {
    permissions,
    roles,
    permissionCodes,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    // Rôles de plateforme
    isPlatformSuperAdmin,
    isPlatformAdmin,
    isPlatformSupport,
    hasPlatformRights,
    // Rôles opérationnels
    isTenantOwner,
    isTenantAdmin,
    isTenantManager,
    isSiteAdmin,
    isSiteManager,
    isSiteSupervisor,
    isQualityAgent,
    isOperator,
    isStoreManager,
    isViewer,
    // Vérifications de contexte
    canManageTenants,
    canManageSites,
    canManageTenantUsers,
    canPerformWeighing,
    canManageStock,
    canPerformQualityAnalysis,
    // Informations de contexte
    getCurrentTenant,
    getCurrentSite,
    getCurrentSiteRole,
    getPermissionsByCategory,
    refreshPermissions,
    initializePermissions
  };
};